import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  LinearProgress,
  MenuItem,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";

const API_BASE = "/api/repricing";

const money = (value) => {
  if (value === null || value === undefined || value === "") return "";
  const n = Number(value);
  return Number.isFinite(n) ? `$${n.toFixed(2)}` : "";
};

const numberText = (value) => {
  const n = Number(value || 0);
  return Number.isFinite(n) ? n.toLocaleString() : "";
};

const percent = (value) => {
  if (value === null || value === undefined || value === "") return "";
  const n = Number(value);
  return Number.isFinite(n) ? `${n.toFixed(2)}%` : "";
};

const profitAfterPrice = (row, price) => {
  const nextPrice = Number(price);
  const currentPrice = Number(row.current_price);
  const currentProfit = Number(row.expected_profit_current);
  if (!Number.isFinite(nextPrice) || !Number.isFinite(currentPrice) || !Number.isFinite(currentProfit)) {
    return row.expected_profit_recommended;
  }
  return currentProfit - (currentPrice - nextPrice);
};

const marginAfterPrice = (row, price) => {
  const nextPrice = Number(price);
  const profit = Number(profitAfterPrice(row, price));
  if (!Number.isFinite(nextPrice) || nextPrice <= 0 || !Number.isFinite(profit)) {
    return row.expected_margin_recommended;
  }
  return (profit / nextPrice) * 100;
};

const reviewPrice = (row, drafts) => {
  const draft = drafts[row.id];
  return draft !== undefined && draft !== "" ? draft : row.operator_override_price || row.recommended_price;
};

const statusColor = (status) => {
  if (status === "approved") return "success";
  if (status === "rejected") return "error";
  if (status === "deferred") return "warning";
  if (status === "manual_override") return "primary";
  if (status === "blocked") return "default";
  return "info";
};

const priorityColor = (priority) => {
  if (priority === "high") return "error";
  if (priority === "medium") return "warning";
  if (priority === "blocked") return "default";
  return "primary";
};

export default function RepricingRunsReview() {
  const [runs, setRuns] = useState([]);
  const [selectedRunId, setSelectedRunId] = useState("");
  const [detail, setDetail] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all");
  const [blockedOnly, setBlockedOnly] = useState(false);
  const [overrideDrafts, setOverrideDrafts] = useState({});
  const [loadingRuns, setLoadingRuns] = useState(false);
  const [loadingDetail, setLoadingDetail] = useState(false);
  const [savingId, setSavingId] = useState(null);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");

  const loadRuns = useCallback(async () => {
    setLoadingRuns(true);
    setError("");
    try {
      const res = await fetch(`${API_BASE}/runs?limit=50`);
      const data = await res.json();
      if (!res.ok || !data.ok) {
        throw new Error(data.error || data.message || "Failed to load repricing runs");
      }
      const nextRuns = Array.isArray(data.rows) ? data.rows : [];
      setRuns(nextRuns);
      setSelectedRunId((current) => current || nextRuns[0]?.id || "");
    } catch (e) {
      setRuns([]);
      setSelectedRunId("");
      setError(e.message || String(e));
    } finally {
      setLoadingRuns(false);
    }
  }, []);

  const loadDetail = useCallback(async () => {
    if (!selectedRunId) {
      setDetail(null);
      return;
    }
    setLoadingDetail(true);
    setError("");
    try {
      const params = new URLSearchParams({
        limit: "500",
        status: statusFilter,
        blocked_only: blockedOnly ? "1" : "0",
      });
      const res = await fetch(`${API_BASE}/runs/${selectedRunId}?${params.toString()}`);
      const data = await res.json();
      if (!res.ok || !data.ok) {
        throw new Error(data.error || data.message || "Failed to load repricing run detail");
      }
      setDetail(data);
    } catch (e) {
      setDetail(null);
      setError(e.message || String(e));
    } finally {
      setLoadingDetail(false);
    }
  }, [blockedOnly, selectedRunId, statusFilter]);

  useEffect(() => {
    loadRuns();
  }, [loadRuns]);

  useEffect(() => {
    loadDetail();
  }, [loadDetail]);

  const saveReview = useCallback(
    async (row, action) => {
      setSavingId(row.id);
      setNotice("");
      setError("");
      try {
        const payload = {
          action,
          operator: "operator",
          note: row.operator_note || "",
        };
        const draft = overrideDrafts[row.id];
        if (draft !== undefined && draft !== "") {
          payload.override_price = draft;
        } else if (action === "manual_override" && row.operator_override_price) {
          payload.override_price = row.operator_override_price;
        }
        const res = await fetch(`${API_BASE}/recommendations/${row.id}/review`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        });
        const data = await res.json();
        if (!res.ok || !data.ok) {
          throw new Error(data.error || data.message || "Failed to save review action");
        }
        setNotice(data.message || "Review action saved locally.");
        await loadDetail();
        await loadRuns();
      } catch (e) {
        setError(e.message || String(e));
      } finally {
        setSavingId(null);
      }
    },
    [loadDetail, loadRuns, overrideDrafts]
  );

  const selectedRun = useMemo(
    () => runs.find((run) => String(run.id) === String(selectedRunId)),
    [runs, selectedRunId]
  );
  const rows = Array.isArray(detail?.rows) ? detail.rows : [];
  const counts = detail?.run?.counts || selectedRun?.counts || {};

  const columns = useMemo(
    () => [
      { field: "sku", headerName: "SKU", width: 120 },
      {
        field: "listing_title",
        headerName: "Listing",
        flex: 1,
        minWidth: 220,
        renderCell: (params) => params.row.listing_title || params.row.card_name || "",
      },
      {
        field: "status",
        headerName: "Status",
        width: 145,
        renderCell: (params) => (
          <Chip size="small" color={statusColor(params.value)} label={params.value || "pending_review"} />
        ),
      },
      {
        field: "recommendation_key",
        headerName: "Recommendation",
        minWidth: 180,
        renderCell: (params) => (
          <Chip size="small" color={priorityColor(params.row.priority)} label={params.value || "monitor"} />
        ),
      },
      {
        field: "no_auto_recommendation_reason",
        headerName: "Review reason",
        minWidth: 260,
        flex: 1,
        renderCell: (params) => (
          <Typography variant="body2">
            {params.value || params.row.recommendation_reason || "-"}
          </Typography>
        ),
      },
      {
        field: "blocked_reason",
        headerName: "Warnings / blockers",
        minWidth: 220,
        flex: 1,
        sortable: false,
        renderCell: (params) => {
          const reasons = Array.isArray(params.row.blocked_reasons) ? params.row.blocked_reasons : [];
          if (!reasons.length && !params.value) return <Typography variant="body2">-</Typography>;
          return (
            <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
              {(reasons.length ? reasons : [params.value]).slice(0, 2).map((reason) => (
                <Chip key={reason} size="small" variant="outlined" label={reason} />
              ))}
              {reasons.length > 2 && <Chip size="small" label={`+${reasons.length - 2}`} />}
            </Stack>
          );
        },
      },
      { field: "current_price", headerName: "Current", width: 105, renderCell: (params) => money(params.value) },
      { field: "ebay_current_price", headerName: "eBay", width: 105, renderCell: (params) => money(params.value) },
      { field: "recommended_price", headerName: "Recommended", width: 130, renderCell: (params) => money(params.value) },
      { field: "min_allowed_price", headerName: "Floor", width: 100, renderCell: (params) => money(params.value) },
      {
        field: "operator_override_price",
        headerName: "Override",
        width: 145,
        renderCell: (params) => (
          <TextField
            size="small"
            type="number"
            value={overrideDrafts[params.row.id] ?? params.value ?? ""}
            onChange={(event) =>
              setOverrideDrafts((current) => ({
                ...current,
                [params.row.id]: event.target.value,
              }))
            }
            inputProps={{ min: 0, step: "0.01" }}
          />
        ),
      },
      { field: "approved_price", headerName: "Approved", width: 110, renderCell: (params) => money(params.value) },
      {
        field: "expected_profit_recommended",
        headerName: "Profit after",
        width: 120,
        renderCell: (params) => money(profitAfterPrice(params.row, reviewPrice(params.row, overrideDrafts))),
      },
      {
        field: "expected_margin_recommended",
        headerName: "Margin after",
        width: 125,
        renderCell: (params) => percent(marginAfterPrice(params.row, reviewPrice(params.row, overrideDrafts))),
      },
      { field: "views_30d", headerName: "Views 30d", width: 105, renderCell: (params) => numberText(params.value) },
      { field: "quantity_sold_30d", headerName: "Sold 30d", width: 100, renderCell: (params) => numberText(params.value) },
      { field: "po_stage", headerName: "PO stage", width: 165 },
      { field: "po_sell_through_pct", headerName: "PO sell-through", width: 135, renderCell: (params) => percent(params.value) },
      { field: "po_capital_recovered_pct", headerName: "Capital rec.", width: 120, renderCell: (params) => percent(params.value) },
      {
        field: "actions",
        headerName: "Review",
        width: 300,
        sortable: false,
        renderCell: (params) => {
          const disabled = savingId === params.row.id;
          const draft = overrideDrafts[params.row.id];
          const overrideValue = draft !== undefined && draft !== "" ? draft : params.row.operator_override_price;
          const hasOverride = overrideValue !== null && overrideValue !== undefined && overrideValue !== "";
          const hasRecommendedPrice = params.row.recommended_price !== null && params.row.recommended_price !== undefined && params.row.recommended_price !== "";
          return (
            <Stack direction="row" spacing={0.75}>
              <Button size="small" variant="contained" disabled={disabled || (!hasRecommendedPrice && !hasOverride)} onClick={() => saveReview(params.row, "accept")}>
                Accept
              </Button>
              <Button size="small" color="error" variant="outlined" disabled={disabled} onClick={() => saveReview(params.row, "reject")}>
                Reject
              </Button>
              <Button size="small" color="warning" variant="outlined" disabled={disabled} onClick={() => saveReview(params.row, "hold")}>
                Hold
              </Button>
              <Button size="small" variant="outlined" disabled={disabled || !hasOverride} onClick={() => saveReview(params.row, "manual_override")}>
                Override
              </Button>
            </Stack>
          );
        },
      },
    ],
    [overrideDrafts, saveReview, savingId]
  );

  return (
    <Stack spacing={2}>
      <Stack direction={{ xs: "column", md: "row" }} spacing={1.5} justifyContent="space-between">
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 700 }}>
            Runs
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Review persisted local recommendation rows. Accepted prices wait in the guarded Push Queue.
          </Typography>
        </Box>
        <Button variant="outlined" size="small" onClick={loadRuns} disabled={loadingRuns || loadingDetail}>
          Refresh
        </Button>
      </Stack>

      {(loadingRuns || loadingDetail) && <LinearProgress />}
      {error && <Alert severity="error">{error}</Alert>}
      {notice && <Alert severity="success">{notice}</Alert>}
      <Alert severity="info">
        Blocked rows in Runs Review are soft review warnings. You can type an override price and approve it locally; the Push Queue still validates eBay sync, offer IDs, and listing status before any live price change.
      </Alert>

      <Stack direction={{ xs: "column", md: "row" }} spacing={1.5}>
        <TextField
          select
          size="small"
          label="Run"
          value={selectedRunId}
          onChange={(event) => setSelectedRunId(event.target.value)}
          sx={{ minWidth: 260 }}
        >
          {runs.map((run) => (
            <MenuItem key={run.id} value={run.id}>
              #{run.id} {run.mode_label || run.run_type} - {run.status}
            </MenuItem>
          ))}
        </TextField>
        <TextField
          select
          size="small"
          label="Status"
          value={statusFilter}
          onChange={(event) => setStatusFilter(event.target.value)}
          sx={{ minWidth: 180 }}
        >
          <MenuItem value="all">All statuses</MenuItem>
          <MenuItem value="pending_review">Pending review</MenuItem>
          <MenuItem value="manual_override">Approved overrides</MenuItem>
          <MenuItem value="approved">Approved</MenuItem>
          <MenuItem value="rejected">Rejected</MenuItem>
          <MenuItem value="deferred">Held</MenuItem>
          <MenuItem value="blocked">Blocked</MenuItem>
        </TextField>
        <TextField
          select
          size="small"
          label="Blocked"
          value={blockedOnly ? "1" : "0"}
          onChange={(event) => setBlockedOnly(event.target.value === "1")}
          sx={{ minWidth: 150 }}
        >
          <MenuItem value="0">All rows</MenuItem>
          <MenuItem value="1">Blocked only</MenuItem>
        </TextField>
      </Stack>

      {!runs.length && !loadingRuns ? (
        <Alert severity="info">
          No repricing runs are available yet. Create a local run from the configured run modes before reviewing recommendations.
        </Alert>
      ) : (
        <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
          <Chip label={`${numberText(counts.recommendation_count)} recommendations`} />
          <Chip color="info" label={`${numberText(counts.pending_review_count)} pending`} />
          <Chip color="primary" label={`${numberText(counts.manual_override_count)} overrides`} />
          <Chip color="success" label={`${numberText(counts.approved_count)} accepted`} />
          <Chip color="warning" label={`${numberText(counts.deferred_count)} held`} />
          <Chip color="error" label={`${numberText(counts.rejected_count)} rejected`} />
          <Chip label={`${numberText(counts.blocked_count)} blocked`} />
        </Stack>
      )}

      <Paper variant="outlined" sx={{ height: 650, width: "100%" }}>
        <DataGrid
          rows={rows}
          columns={columns}
          getRowId={(row) => row.id}
          loading={loadingRuns || loadingDetail}
          disableRowSelectionOnClick
          pageSizeOptions={[25, 50, 100]}
          initialState={{
            pagination: { paginationModel: { pageSize: 25, page: 0 } },
            sorting: { sortModel: [{ field: "status", sort: "asc" }] },
          }}
        />
      </Paper>
    </Stack>
  );
}
